import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  BookOpen, Lightbulb, ArrowRight, TrendingUp, Target, Zap, Award,
  CheckCircle, Lock, Cpu, Activity, ZapOff, MessageSquare, X
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import GamificationNeuralGrid from '../components/GamificationNeuralGrid';

const VICTORY_TYPES = [
  { id: "first_doc_ai", label: "DOC_GEN_V1" },
  { id: "deep_research", label: "DEEP_RES_PROTO" },
  { id: "image_gen", label: "IMG_SYNTH" },
  { id: "video_gen", label: "VID_RENDER" },
  { id: "presentation_gen", label: "PPT_AUTO" },
  { id: "site_gen", label: "WEB_DEPLOY" },
  { id: "automation_gen", label: "AUTO_FLOW" },
  { id: "personal_agent", label: "AGENT_01" },
  { id: "api_usage", label: "API_LINK" },
  { id: "full_app", label: "APP_BUILD" },
  { id: "multi_agent", label: "SWARM_NET" },
  { id: "biz_ai_plan", label: "BIZ_STRAT" },
  { id: "ai_first_area", label: "DEPT_TRANS" },
  { id: "ai_first_biz", label: "CORP_TRANS" },
  // New Achievements
  { id: "prompt_architect", label: "PROMPT_ARCH" },
  { id: "data_weaver", label: "DATA_WEAVE" },
  { id: "tool_master", label: "TOOL_MAST" },
  { id: "efficiency_expert", label: "EFF_EXP" },
  { id: "ai_strategist", label: "STRAT_AI" }
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function Dashboard({ user }) {
  // Master Design System v2.0
  const [modules, setModules] = useState([]);
  const [userLevel, setUserLevel] = useState(null);
  const [stats, setStats] = useState(null);

  // Mentor Contact State
  const [isMentorModalOpen, setIsMentorModalOpen] = useState(false);
  const [messageSubject, setMessageSubject] = useState('');
  const [messageBody, setMessageBody] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [modulesRes, levelRes, statsRes] = await Promise.all([
          axios.get(`${API}/modules`, { withCredentials: true }),
          axios.get(`${API}/users/level`, { withCredentials: true }),
          axios.get(`${API}/users/stats`, { withCredentials: true })
        ]);
        setModules(modulesRes.data.slice(0, 6));
        setUserLevel(levelRes.data);
        setStats(statsRes.data);
      } catch (error) {
        console.error('Data Fetch Error:', error);
      }
    };
    fetchData();
  }, []);

  const handleSendMessage = async () => {
    if (!messageBody.trim()) {
      toast.error("Por favor, digite uma mensagem.");
      return;
    }

    try {
      setSendingMessage(true);
      await axios.post(`${API}/chat/contact-admin`, {
        subject: messageSubject,
        message: messageBody
      }, { withCredentials: true });

      toast.success("Mensagem enviada com sucesso!");
      setIsMentorModalOpen(false);
      setMessageSubject('');
      setMessageBody('');
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Erro ao enviar mensagem.");
    } finally {
      setSendingMessage(false);
    }
  };

  const progressPercentage = stats ? (stats.modules_started / 6) * 100 : 0;

  return (
    <div className="min-h-screen bg-transparent relative">
      {/* Background provided by body CSS (Engineering Grid) */}
      <Navigation user={user} />

      <main className="container mx-auto px-4 md:px-8 py-16 relative z-10">
        <div className="flex flex-col md:flex-row justify-between items-end mb-12 border-b border-white/10 pb-6">
          <div>
            <div className="flex items-center gap-2 mb-2 text-aift-orange text-xs tracking-widest uppercase">
              <Cpu size={14} />
              <span>System Active</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold tracking-tighter leading-none mb-2 font-heading text-aift-cream uppercase" data-testid="dashboard-title">
              Olá, {user?.name?.split(' ')[0]}
            </h1>
            <p className="text-lg text-aift-blue font-mono">
              // Protocolo de Singularidade iniciado...
            </p>
          </div>

          <div className="text-right hidden md:block">
            <div className="text-xs text-muted-foreground font-mono">ID: {user?._id?.substring(0, 8)}...</div>
            <div className="text-xs text-muted-foreground font-mono">LATENCY: 24ms</div>
          </div>
        </div>

        {userLevel && stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-16">

            {/* LEVEL CARD - ORANGE */}
            <div className="tech-card-shadow corner-accent p-6 group">
              <div className="flex justify-between items-start mb-4">
                <div className="text-aift-orange">
                  <Award className="h-6 w-6" />
                </div>
                <span className="text-xs font-mono text-aift-orange/70">STATUS_NIVEL</span>
              </div>
              <div className="text-4xl font-bold font-heading text-white mb-2">{userLevel.level}</div>
              <div className="w-full bg-white/5 h-1 mb-2">
                <div className="bg-aift-orange h-1" style={{ width: `${userLevel.score}%` }}></div>
              </div>
              <div className="flex justify-between text-[10px] font-mono text-muted-foreground uppercase">
                <span>XP: {userLevel.xp}</span>
                <span>Target: {userLevel.nextLevelScore}</span>
              </div>
            </div>

            {/* MODULES CARD - BLUE */}
            <div className="tech-card-shadow p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="text-aift-blue">
                  <BookOpen className="h-6 w-6" />
                </div>
                <span className="text-xs font-mono text-aift-blue/70">MODULOS_CARREG</span>
              </div>
              <div className="text-4xl font-bold font-heading text-white mb-2">{stats.modules_started}/6</div>
              <div className="text-[10px] font-mono text-muted-foreground uppercase mt-4">
                Conteúdo Técnica
              </div>
            </div>

            {/* TASKS CARD - GREEN */}
            <div className="tech-card-shadow p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="text-aift-green">
                  <Activity className="h-6 w-6" />
                </div>
                <span className="text-xs font-mono text-aift-green/70">TAREFAS_PEND</span>
              </div>
              <div className="text-4xl font-bold font-heading text-white mb-2">{stats.submissions_pending || 0}</div>
              <div className="text-[10px] font-mono text-muted-foreground uppercase mt-4">
                Requisições de Correção
              </div>
            </div>

            {/* TOOLS CARD - PINK */}
            <div className="tech-card-shadow corner-accent-br p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="text-aift-pink">
                  <Zap className="h-6 w-6" />
                </div>
                <span className="text-xs font-mono text-aift-pink/70">TOOLS_AVALIADAS</span>
              </div>
              <div className="text-4xl font-bold font-heading text-white mb-2">{stats.tools_evaluated || 0}</div>
              <div className="text-[10px] font-mono text-muted-foreground uppercase mt-4">
                Ferramentas Mapeadas
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          <div className="lg:col-span-2">
            <div className="tech-card-shadow p-8 relative overflow-hidden">
              {/* Visual Sub-label as seen in reference */}
              <div className="absolute top-0 right-0 bg-primary/10 text-primary text-[10px] px-2 py-0.5 font-mono">NEURAL_GRID_v2.1</div>

              <div className="flex items-center gap-3 mb-8">
                <TrendingUp className="text-aift-orange h-5 w-5" />
                <h2 className="text-xl font-bold font-heading text-white uppercase tracking-tight">Log_de_Vitórias</h2>
                <div className="ml-auto text-[10px] font-mono text-aift-orange/50 bg-aift-orange/5 px-2">
                  [{stats?.achievements?.length || 0} / 19] DESBLOQUEADAS
                </div>
              </div>

              <div className="grid-pattern-subtle p-4 border border-white/5">
                <GamificationNeuralGrid achievements={stats?.achievements || []} />
              </div>
            </div>
          </div>

          <div className="space-y-8">
            <div className="tech-card-shadow p-8 border-l border-white/5">
              <h2 className="text-xl font-bold font-heading text-white uppercase tracking-tight mb-8 font-mono tracking-tighter">// Acesso Rápido</h2>

              <div className="space-y-4">
                <Link to="/modules" className="flex items-center justify-between p-4 bg-white/5 hover:bg-white/10 border-l border-aift-blue transition-all group overflow-hidden relative">
                  <div className="flex items-center gap-4 relative z-10">
                    <BookOpen className="h-5 w-5 text-aift-blue" />
                    <span className="font-mono text-sm uppercase tracking-wide">Diretorio_Modulos</span>
                  </div>
                  <ArrowRight className="h-4 w-4 text-aift-blue opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all relative z-10" />
                  <div className="absolute top-0 right-0 w-1.5 h-1.5 bg-aift-blue/50" />
                </Link>
                <Link to="/ai-tools" className="flex items-center justify-between p-4 bg-white/5 hover:bg-white/10 border-l border-aift-pink transition-all group overflow-hidden relative">
                  <div className="flex items-center gap-4 relative z-10">
                    <Zap className="h-5 w-5 text-aift-pink" />
                    <span className="font-mono text-sm uppercase tracking-wide">Base_de_Tools</span>
                  </div>
                  <ArrowRight className="h-4 w-4 text-aift-pink opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all relative z-10" />
                  <div className="absolute top-0 right-0 w-1.5 h-1.5 bg-aift-pink/50" />
                </Link>
                <Link to="/profile" className="flex items-center justify-between p-4 bg-white/5 hover:bg-white/10 border-l border-aift-green transition-all group overflow-hidden relative">
                  <div className="flex items-center gap-4 relative z-10">
                    <TrendingUp className="h-5 w-5 text-aift-green" />
                    <span className="font-mono text-sm uppercase tracking-wide">Estat_Usuario</span>
                  </div>
                  <ArrowRight className="h-4 w-4 text-aift-green opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all relative z-10" />
                  <div className="absolute top-0 right-0 w-1.5 h-1.5 bg-aift-green/50" />
                </Link>

                <div
                  onClick={() => setIsMentorModalOpen(true)}
                  className="flex items-center justify-between p-4 bg-white/5 hover:bg-white/10 border-l border-primary transition-all group overflow-hidden relative cursor-pointer"
                >
                  <div className="flex items-center gap-4 relative z-10">
                    <MessageSquare className="h-5 w-5 text-primary" />
                    <span className="font-mono text-sm uppercase tracking-wide">Contato_Mentor</span>
                  </div>
                  <ArrowRight className="h-4 w-4 text-primary opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all relative z-10" />
                  <div className="absolute top-0 right-0 w-1.5 h-1.5 bg-primary/50" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="pt-12 relative">
          {/* Section Dotted Background */}
          <div className="absolute inset-0 grid-dot-pattern pointer-events-none opacity-40 -z-10" />

          <div className="flex items-end justify-between mb-8 border-b border-white/10 pb-4">
            <div>
              <div className="text-[10px] font-mono text-aift-blue mb-1 uppercase tracking-[0.2em]">PATHWAY_SYSTEM</div>
              <h2 className="text-4xl font-bold tracking-tight font-heading text-aift-cream uppercase">Módulos em Destaque</h2>
            </div>
            <Link to="/modules">
              <button className="text-xs font-mono text-aift-orange hover:text-white transition-colors uppercase custom-hover-effect">
                [Ver Toda a Base]
              </button>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {modules.map((module, index) => (
              <Link key={module.module_id} to={`/modules/${module.module_id}`} data-testid={`module-card-${module.module_id}`}>
                <div className="tech-card p-6 h-full hover:border-primary/30 group transition-all duration-300 corner-accent">
                  <div className="flex justify-between mb-4">
                    <div className="text-[10px] font-mono text-aift-blue border border-aift-blue/20 px-2 py-0.5">MOD_0{index + 1}</div>
                    <Lock className="w-4 h-4 text-muted-foreground/30" />
                  </div>
                  <h3 className="text-xl font-bold mb-2 font-heading group-hover:text-primary transition-colors uppercase">
                    {module.title}
                  </h3>
                  <p className="text-sm font-mono text-muted-foreground mb-4 line-clamp-3 leading-relaxed">{module.description}</p>
                  <div className="mt-auto pt-4 border-t border-white/5 flex items-center gap-2 text-[10px] font-mono text-aift-cream uppercase group-hover:text-primary">
                    <span>Executar Protocolo</span> <ArrowRight className="h-3 w-3" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </main >

      {/* Mentor Contact Modal */}
      {
        isMentorModalOpen && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setIsMentorModalOpen(false)}>
            <Card className="w-full max-w-lg p-0 rounded-none border-primary/30 bg-[#0B0E14] tech-card-shadow" onClick={e => e.stopPropagation()}>
              <div className="p-6 border-b border-white/10 flex justify-between items-center">
                <div>
                  <div className="text-[10px] font-mono text-primary mb-1 uppercase tracking-[0.2em]">DIRECT_LINK</div>
                  <h3 className="text-xl font-bold font-heading text-white uppercase">Fale com o Mentor</h3>
                </div>
                <button onClick={() => setIsMentorModalOpen(false)} className="text-muted-foreground hover:text-white">
                  <X size={24} />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="text-xs font-mono text-muted-foreground uppercase mb-2 block">Assunto</label>
                  <Input
                    value={messageSubject}
                    onChange={(e) => setMessageSubject(e.target.value)}
                    placeholder="Ex: Dúvida no Módulo 3"
                    className="rounded-none bg-white/5 border-white/10 focus:border-primary font-mono text-sm"
                  />
                </div>

                <div>
                  <label className="text-xs font-mono text-muted-foreground uppercase mb-2 block">Mensagem</label>
                  <Textarea
                    value={messageBody}
                    onChange={(e) => setMessageBody(e.target.value)}
                    placeholder="Descreva sua dúvida ou solicitação..."
                    className="rounded-none bg-white/5 border-white/10 focus:border-primary font-mono text-sm min-h-[150px]"
                  />
                </div>
              </div>

              <div className="p-6 border-t border-white/10 flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => setIsMentorModalOpen(false)}
                  className="rounded-none font-mono text-xs uppercase"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleSendMessage}
                  disabled={sendingMessage}
                  className="rounded-none font-mono text-xs uppercase bg-primary text-black font-bold hover:bg-primary/90"
                >
                  {sendingMessage ? 'Transmitindo...' : 'Enviar Mensagem'}
                </Button>
              </div>
            </Card>
          </div>
        )
      }
    </div >
  );
}